// ─────────────────────────────────────────────────────────────
// oaAuth.js — one sign-in across every OA module (OA Auth Task 05)
// ─────────────────────────────────────────────────────────────
// THIS FILE IS IDENTICAL in oa-engine, oa-scc, oa-proposal-generator,
// oa-client-dashboard and oa-vendor-portal (src/lib/oaAuth.js). Change all
// five together, or the modules stop sharing a session.
//
// What it does
//   • Session lives in a cookie (via @supabase/ssr createBrowserClient), not
//     localStorage. On *.obviousadvantage.ca the cookie is scoped to
//     .obviousadvantage.ca, so signing in on one module signs in all of them
//     on this device. Anywhere else (localhost, *.netlify.app deploy
//     previews) the cookie is host-only, so a preview signs in on its own.
//   • Default cookie name for the project (sb-<ref>-auth-token). Never set a
//     custom storageKey / cookie name — the modules would stop seeing each
//     other's session.
//   • Migration: if there's no cookie session but this origin still has the
//     old localStorage session, it's moved into the cookie (setSession) and
//     the old keys are removed. If that fails the user just sees sign-in.
//   • Sign-in is the emailed 6-digit code only (verifyOtp); email-link
//     redirects are not handled (detectSessionInUrl: false).
//   • Sign-out is scope 'local': this device, every module; other devices
//     stay signed in.
//   • Another module can sign out (or switch user) behind this tab's back.
//     watchSharedSession() re-reads the cookie when the tab regains focus
//     and fires SIGNED_OUT locally if it's gone.
//   • authedFetch / invokeFunction: send the current (auto-refreshed) access
//     token; on a 401, refresh once and retry once; if it still fails, sign
//     out locally so the app shows its normal sign-in screen.
//
// Access rules are NOT here — each app keeps its own gate.

import { createBrowserClient } from '@supabase/ssr'

const SHARED_DOMAIN = 'obviousadvantage.ca'
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60   // browser maximum; renewed on every token refresh

let client = null
let ready = null

function cookieOptions() {
  const host = window.location.hostname
  const base = { path: '/', sameSite: 'lax', maxAge: MAX_AGE_SECONDS }
  if (host === SHARED_DOMAIN || host.endsWith('.' + SHARED_DOMAIN)) {
    return { ...base, domain: '.' + SHARED_DOMAIN, secure: true }
  }
  // localhost / deploy previews: host-only cookie (no domain).
  return { ...base, secure: window.location.protocol === 'https:' }
}

// supabase-js's default storage key for the project — the old localStorage
// session lived here, and the cookie uses the same name.
function defaultAuthKey(url) {
  return `sb-${new URL(url).hostname.split('.')[0]}-auth-token`
}

async function migrateLegacySession(supabase, url) {
  const key = defaultAuthKey(url)
  let legacyKeys = []
  let raw = null
  try {
    legacyKeys = Object.keys(window.localStorage).filter((k) => k.startsWith(key))
    raw = window.localStorage.getItem(key)
  } catch { return }
  if (!legacyKeys.length) return

  let keep = false
  try {
    const { data } = await supabase.auth.getSession()
    if (!data?.session && raw) {
      const stored = JSON.parse(raw)
      const s = stored?.currentSession || stored
      if (s?.access_token && s?.refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token: s.access_token,
          refresh_token: s.refresh_token,
        })
        // Offline: keep the old keys so the next load can try again.
        if (error && error.name === 'AuthRetryableFetchError') keep = true
        if (error) console.warn('[oaAuth] legacy session not migrated:', error.message)
      }
    }
  } catch (e) {
    console.warn('[oaAuth] legacy session migration failed:', e?.message || e)
  }
  if (!keep) {
    try { legacyKeys.forEach((k) => window.localStorage.removeItem(k)) } catch {}
  }
}

// Create (once) the shared browser client. Returns the client; apps wait on
// authReady() before their first getSession() / onAuthStateChange so the
// legacy migration has finished.
export function initOaAuth(url, anonKey) {
  if (client) return client
  client = createBrowserClient(url, anonKey, {
    cookieOptions: cookieOptions(),
    // Sign-in is the 6-digit code only (verifyOtp). No module consumes an
    // email-link redirect (?code= / #access_token), so URL detection is off.
    auth: { detectSessionInUrl: false },
  })
  ready = migrateLegacySession(client, url)
  return client
}

export function authReady() {
  return ready || Promise.resolve()
}

function requireClient() {
  if (!client) throw new Error('[oaAuth] initOaAuth() has not been called')
  return client
}

// Sign out of every module on this device (not other devices).
export function signOutHere() {
  return requireClient().auth.signOut({ scope: 'local' })
}

// Another module shares this session, so it can sign out — or sign in as
// someone else — while this tab is in the background. When the tab becomes
// visible / focused again, re-read the cookie:
//   • gone            → signOutHere(): clears this tab and fires SIGNED_OUT,
//                       so the app's existing handler shows sign-in.
//   • different user  → onUserChange(session): the app re-runs its gate.
// Returns an unsubscribe function.
export function watchSharedSession(onUserChange) {
  const supabase = requireClient()
  let knownUid = null
  let checking = false

  const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
    knownUid = session?.user?.id || null
  })

  async function recheck() {
    if (checking || document.visibilityState === 'hidden') return
    checking = true
    try {
      const { data } = await supabase.auth.getSession()
      const uid = data?.session?.user?.id || null
      if (!uid && knownUid) {
        knownUid = null
        await supabase.auth.signOut({ scope: 'local' })
      } else if (uid && uid !== knownUid) {
        knownUid = uid
        onUserChange?.(data.session)
      }
    } catch (e) {
      console.warn('[oaAuth] session re-check failed:', e?.message || e)
    } finally {
      checking = false
    }
  }

  document.addEventListener('visibilitychange', recheck)
  window.addEventListener('focus', recheck)
  return () => {
    document.removeEventListener('visibilitychange', recheck)
    window.removeEventListener('focus', recheck)
    sub?.subscription?.unsubscribe?.()
  }
}

// Current access token, refreshed first if it's about to expire.
export async function getAccessToken() {
  const { data } = await requireClient().auth.getSession()
  return data?.session?.access_token || null
}

// After a 401: refresh once. Returns the new token, or null after signing out
// locally (refresh rejected) so the app falls back to its sign-in screen. A
// network failure during refresh does not sign anyone out.
async function refreshOrSignOut() {
  const supabase = requireClient()
  const { data, error } = await supabase.auth.refreshSession()
  if (data?.session?.access_token) return data.session.access_token
  if (error?.name !== 'AuthRetryableFetchError') await signOutHere()
  return null
}

// fetch() for edge functions / Netlify functions that take the user's session.
// Sets Authorization: Bearer <current token> (other headers are kept). Only
// for session-authenticated calls — anonymous/portal-token calls should use
// plain fetch so a 401 there never signs the user out.
export async function authedFetch(input, init = {}) {
  const send = (token) => {
    const headers = new Headers(init.headers || {})
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return fetch(input, { ...init, headers })
  }
  const token = await getAccessToken()
  const res = await send(token)
  if (res.status !== 401 || !token) return res
  const fresh = await refreshOrSignOut()
  if (!fresh) return res
  const retry = await send(fresh)
  if (retry.status === 401) await signOutHere()
  return retry
}

// supabase.functions.invoke with the same refresh-once / retry-once rule.
export async function invokeFunction(name, options) {
  const supabase = requireClient()
  const first = await supabase.functions.invoke(name, options)
  if (first.error?.context?.status !== 401) return first
  if (!(await getAccessToken())) return first   // wasn't signed in — nothing to refresh
  const fresh = await refreshOrSignOut()
  if (!fresh) return first
  const retry = await supabase.functions.invoke(name, options)
  if (retry.error?.context?.status === 401) await signOutHere()
  return retry
}
